<template>
  <div class="gc-card">
    <div class="gc-header">
      <img class="gc-icon" src="https://img.icons8.com/color/48/google-logo.png" alt="Google Calendar" />
      <div>
        <h4>Google Calendar Integration</h4>
        <p style="font-weight: 600;">
          Connect your Google Calendar to automatically sync meetings and events
        </p>
      </div>
    </div>

    <div class="gc-actions">
      <div v-if="initializing" class="gc-skeleton">
        <div class="skeleton-bar"></div>
      </div>

      <button v-else-if="!isConnected" class="gc-btn primary" @click="connect" :disabled="loadings.connect">
        <img src="https://img.icons8.com/color/48/google-logo.png" alt="Google" />
        {{ loadings.connect ? 'Connecting...' : 'Connect Google Calendar' }}
      </button>

      <div v-else class="gc-connected-bar">
        <div class="left">
          <span class="check">✔</span>
          <div class="text">
            <strong>Connected</strong>
            <small>{{ userEmail }}</small>
          </div>
        </div>

        <button class="gc-btn outline" @click="disconnect" :disabled="loadings.disconnect">
          <i class="mdi mdi-link-variant-off"></i>
          {{ loadings.disconnect ? 'Disconnecting...' : 'Disconnect' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as firebase from 'firebase/app'
import { serverURL } from '../../assets/config/constants'

export default {
  name: 'GoogleCalendarIntegration',

  data() {
    return {
      isConnected: false,
      userEmail: '',
      initializing: true,
      loadings: {
        connect: false,
        disconnect: false,
      },
    }
  },

  mounted() {
    this.handleOAuthCallback()
    this.restoreSession()
  },

  methods: {
    handleOAuthCallback() {
      console.log("Checking for OAuth callback parameters...")

      const hash = window.location.hash
      const queryString = hash.includes('?') ? hash.split('?')[1] : ''
      const params = new URLSearchParams(queryString)

      const gcConnected = params.get('gc_connected')
      const accessToken = params.get('accessToken')
      const email = params.get('email')
      const gcError = params.get('gc_error')

      console.log(params.toString())

      if (gcConnected && accessToken && email) {
        localStorage.setItem('gc_access_token', accessToken)
        localStorage.setItem('gc_user_email', email)

        this.isConnected = true
        this.userEmail = email

        window.location.hash = window.location.hash.split('?')[0]

        this.$emit('connected')
      }

      if (gcError) {
        alert('Google Calendar connection failed. Please try again.')
        window.location.hash = window.location.hash.split('?')[0]
      }
    },

    restoreSession() {
      const token = localStorage.getItem('gc_access_token')
      const email = localStorage.getItem('gc_user_email')
      if (token && email) {
        this.isConnected = true
        this.userEmail = email
      }
      this.initializing = false
    },

    connect() {
      this.loadings.connect = true
      const uid = firebase.auth().currentUser.uid
      const companyEmail = firebase.auth().currentUser.email
      const frontendUrl = window.location.origin

      window.location.href =
        `${serverURL}/google-calendar/authorize?uid=${uid}&companyEmail=${companyEmail}&frontendUrl=${encodeURIComponent(frontendUrl)}`
    },

    async disconnect() {
      this.loadings.disconnect = true

      try {
        const currentUser = firebase.auth().currentUser
        if (currentUser) {
          await fetch(`${serverURL}/google-calendar/disconnect`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              uid: currentUser.uid,
              companyEmail: currentUser.email
            })
          })
        }
      } catch (err) {
        console.warn('Disconnect failed:', err)
      } finally {
        this.loadings.disconnect = false
      }

      this.clearSession()
      this.$emit('disconnected')
    },

    clearSession() {
      this.isConnected = false
      this.userEmail = ''
      localStorage.removeItem('gc_access_token')
      localStorage.removeItem('gc_user_email')
    },
  },
}
</script>

<style scoped>
.gc-card {
  border: 1px solid #e6e8eb;
  border-radius: 10px;
  padding: 20px;
  background: #fff;
}

.gc-header {
  display: flex;
  gap: 14px;
  margin-bottom: 16px;
}

.gc-icon {
  width: 40px;
  height: 40px;
}

.gc-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.gc-header p {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.gc-actions {
  margin-top: 12px;
}

.gc-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 14px;
}

.gc-btn i {
  margin-right: 6px;
}

.gc-btn img {
  width: 18px;
  height: 18px;
}

.gc-btn.primary {
  background: #1057f2;
  color: #fff;
}

.gc-btn.primary:hover {
  background: #1e4fd8;
}

.gc-btn.danger {
  background: #ef4444;
  color: #fff;
}

.gc-connected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.status {
  font-size: 14px;
  font-weight: 500;
  color: #16a34a;
}

.buttons {
  display: flex;
  gap: 10px;
}

.gc-connected-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-radius: 8px;
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
}

.gc-connected-bar .left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #16a34a;
  color: #16a34a;
  font-weight: bold;
  font-size: 12px;
}

.text strong {
  display: block;
  font-size: 14px;
  color: #065f46;
}

.text small {
  font-size: 12px;
  color: #047857;
}

.gc-btn.outline {
  background: #fff;
  border: 1px solid #d1d5db;
  color: #374151;
  font-weight: bold;
}

.gc-btn.outline:hover {
  background: #f9fafb;
}
.gc-skeleton {
  height: 40px;
  display: flex;
  align-items: center;
}

.skeleton-bar {
  width: 220px;
  height: 38px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>


// WEBPACK FOOTER //
// src/components/GoogleCalender/GoogleCalendarIntegration.vue